import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Save, Sparkles } from 'lucide-react'
import { clients, type ClientSettings } from '../../api'

/**
 * AI auto-folder configuration — Studio surface.
 *
 * Why a Studio page instead of /settings: superusers (and anyone with
 * Studio access) need to inspect and edit the firm-context per-client,
 * including for tenants other than their own. The /settings page is
 * the user's OWN-TENANT view; this is the cross-tenant operator view.
 *
 * Picks the client via dropdown (any client visible to the user;
 * superusers see all). Edits apply to that client's
 * `ai_auto_folder_enabled` + `ai_domain_context`.
 */
export default function AIConfig() {
  const list = useQuery({ queryKey: ['clients'], queryFn: () => clients.list() })
  const allClients = list.data ?? []
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Default to first client when the list loads.
  useEffect(() => {
    if (selectedId == null && allClients.length > 0) {
      setSelectedId(allClients[0].id)
    }
  }, [allClients, selectedId])

  const selected = useMemo(
    () => allClients.find((c) => c.id === selectedId) ?? null,
    [allClients, selectedId],
  )

  return (
    <div className="me-scroll mx-auto max-w-3xl overflow-auto p-6">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="me-text-mute" />
        <h1 className="text-[18px] font-semibold me-text">AI auto-folder configuration</h1>
      </div>
      <div className="mt-1 text-[12px] me-text-mute">
        Per-client toggle and the firm-context string the auto-folder AI sees
        on every filing decision. Visible to operators with Studio access.
      </div>

      <div className="mt-6 me-card p-5">
        <div className="me-section-h">Client</div>
        <div className="text-[12px] me-text-mute mb-3">
          Pick which tenant's AI auto-folder config to view or edit.
        </div>
        <select
          value={selectedId ?? ''}
          onChange={(e) => setSelectedId(Number(e.target.value))}
          className="me-input"
          disabled={list.isLoading}
        >
          {allClients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </div>

      {selected && <ClientAIConfigForm client={selected} />}

      {list.isLoading && (
        <div className="mt-6 me-text-mute text-[12px]">Loading clients…</div>
      )}
    </div>
  )
}

function ClientAIConfigForm({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  // Defensive defaults — if the API response is missing these fields
  // (older deploy, transient gap), we fall back instead of crashing
  // the render later when reading .length on undefined.
  const [enabled, setEnabled] = useState<boolean>(client.ai_auto_folder_enabled ?? false)
  const [domain, setDomain] = useState<string>(client.ai_domain_context ?? '')

  // Reset state when the client selection changes.
  useEffect(() => {
    setEnabled(client.ai_auto_folder_enabled ?? false)
    setDomain(client.ai_domain_context ?? '')
  }, [client.id, client.ai_auto_folder_enabled, client.ai_domain_context])

  const save = useMutation({
    mutationFn: () => clients.update(client.id, {
      ai_auto_folder_enabled: enabled,
      ai_domain_context: domain,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const isDirty =
    enabled !== (client.ai_auto_folder_enabled ?? false) ||
    domain !== (client.ai_domain_context ?? '')

  return (
    <>
      <div className="mt-6 me-card p-5">
        <div className="me-section-h">Enable / disable</div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-0.5 cursor-pointer"
          />
          <div>
            <div className="font-medium me-text">
              Run AI auto-folder for <span className="me-mono">{client.code}</span>
            </div>
            <div className="text-[11.5px] me-text-mute leading-snug">
              When enabled, every matter touched by artifact processing gets
              evaluated by the AI for folder placement (subject to per-matter
              idempotency — unchanged matters are skipped). User-assigned
              folders are never overridden.
              {!enabled && (
                <span className="block mt-1 text-[color:var(--me-text)]">
                  Currently OFF — no AI filing for this tenant.
                </span>
              )}
            </div>
          </div>
        </label>
      </div>

      <div className="mt-4 me-card p-5">
        <div className="me-section-h">Domain context</div>
        <div className="text-[12px] me-text-mute mb-3 leading-snug">
          Firm-specific guidance fed to the auto-folder prompt. Tells the AI
          what kinds of matters this firm handles and any structural
          preferences (subject-style vs. counterparty-style filing, custom
          top-level categories, etc.). Plain text, no schema.
        </div>
        <textarea
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="Higher-ed general counsel office. Categories include the universal legal-practice ones (Contracts, Employment, Litigation, Compliance, Real Estate, IP, Governance, Tax, Privacy) plus higher-ed specifics (Student Affairs, Faculty Matters, Athletics, Title IX). Subject-style preferred — no top-level Vendors..."
          className="me-input font-normal"
          rows={16}
          style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
        />
        <div className="text-[10.5px] me-text-mute mt-1">
          {domain.length.toLocaleString()} characters — applies on the next
          auto-folder task; no rebuild needed.
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          className="me-btn me-btn-primary"
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>
    </>
  )
}
